import { useMemo, useState } from 'react';
import { useChargePoints, useStationMutations, useStations } from '../lib/queries';
import type { LiveSite } from '../data/live';
import type { ChargePoint } from '../data/types';
import { chargePointLabel, chargePointTone, freeNow } from '../data/types';
import { Chip, Empty, Loading, Pill, Tile } from '../components/ui';
import { errorText, loadFailedHint } from '../lib/errors';

/*
 * Stations — the site level.
 *
 * This is the one thing on the network that nothing creates by itself. A charge
 * point calls `ChargingStation.create` the instant it opens a websocket, which
 * is why five connectivity probes sit on this network as real hardware; the
 * site it stands on exists only because an operator said so here.
 *
 * So the screen is built around the gap that causes: charge points that have
 * connected and belong to no address. That count is the first thing on it.
 */

type Draft = Partial<LiveSite> & { name: string };

const EMPTY: Draft = {
  name: '', address: '', city: '', state: '', postalCode: '', country: '',
  lat: null, lng: null, timezone: 'UTC', notes: '', environment: 'test',
};

/** A modal that closes on backdrop click and on Escape. */
function Modal({ label, children, onClose }: {
  label: string; children: React.ReactNode; onClose: () => void;
}) {
  return (
    <div
      role="dialog" aria-label={label}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.55)',
        display: 'grid', placeItems: 'center', padding: 16, zIndex: 40,
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}
    >
      <div className="card" style={{ width: '100%', maxWidth: 480, maxHeight: '88vh', overflow: 'auto' }}>
        {children}
      </div>
    </div>
  );
}

/**
 * Add or edit a station.
 *
 * Coordinates are both or neither. Half a pair is not a position, and a site at
 * (9.02, null) would plot on the equator off the coast of Africa — which looks
 * like an answer rather than a missing one. The server refuses it too.
 */
function StationForm({ initial, pending, error, onClose, onSubmit }: {
  initial: Draft;
  pending: boolean;
  error: string | null;
  onClose: () => void;
  onSubmit: (v: Draft) => void;
}) {
  const [d, setD] = useState<Draft>(initial);
  const set = (k: keyof Draft) => (v: string) =>
    setD((p) => ({ ...p, [k]: v }));

  const [lat, setLat] = useState(initial.lat == null ? '' : String(initial.lat));
  const [lng, setLng] = useState(initial.lng == null ? '' : String(initial.lng));
  const latN = lat.trim() === '' ? null : Number(lat);
  const lngN = lng.trim() === '' ? null : Number(lng);

  const name = d.name.trim();
  const bad = name.length < 2 ? 'Give the station a name of at least two characters.'
    : (latN == null) !== (lngN == null)
      ? 'Latitude and longitude go together, or neither.'
      : latN != null && !(Number.isFinite(latN) && Math.abs(latN) <= 90)
        ? 'Latitude runs from -90 to 90.'
        : lngN != null && !(Number.isFinite(lngN) && Math.abs(lngN) <= 180)
          ? 'Longitude runs from -180 to 180.'
          /* The server refuses this too. Blocking the button as well means the
             operator finds out while looking at the field, not after a save. */
          : d.environment === 'prod' && (latN == null || lngN == null)
            ? 'A live site needs coordinates - without them drivers get a name and nothing to navigate to.'
            : null;

  const field = (k: keyof Draft, label: string, placeholder = '') => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <label className="li-sub" htmlFor={`st-${k}`}>{label}</label>
      <input
        id={`st-${k}`} className="field" placeholder={placeholder}
        value={(d[k] as string | null) ?? ''}
        onChange={(e) => set(k)(e.target.value)}
      />
    </div>
  );

  return (
    <Modal label={initial.id ? 'Edit station' : 'Add station'} onClose={onClose}>
      <div className="li-title" style={{ fontSize: 15 }}>
        {initial.id ? 'Edit station' : 'Add a station'}
      </div>
      <div className="li-sub" style={{ marginBottom: 12 }}>
        A site with an address. Charge points are assigned to it from the Charge
        points screen — this is the place they stand, not the hardware.
      </div>

      <label className="li-sub" htmlFor="st-name">Name</label>
      <input id="st-name" className="field" value={d.name} autoFocus
             placeholder="Bole Airport Hub" onChange={(e) => set('name')(e.target.value)}
             style={{ marginBottom: 10 }} />

      {field('address', 'Address', 'Airport Road, near Terminal 2')}
      <div style={{ height: 10 }} />
      <div style={{ display: 'flex', gap: 8 }}>
        {field('city', 'City', 'Addis Ababa')}
        {field('state', 'Region / state')}
      </div>
      <div style={{ height: 10 }} />
      <div style={{ display: 'flex', gap: 8 }}>
        {field('postalCode', 'Postal code')}
        {field('country', 'Country', 'Ethiopia')}
      </div>

      <div style={{ height: 10 }} />
      <div style={{ display: 'flex', gap: 8 }}>
        <div style={{ flex: 1 }}>
          <label className="li-sub" htmlFor="st-lat">Latitude</label>
          <input id="st-lat" className="field mono" value={lat} inputMode="decimal"
                 placeholder="9.0054" onChange={(e) => setLat(e.target.value)} />
        </div>
        <div style={{ flex: 1 }}>
          <label className="li-sub" htmlFor="st-lng">Longitude</label>
          <input id="st-lng" className="field mono" value={lng} inputMode="decimal"
                 placeholder="38.7636" onChange={(e) => setLng(e.target.value)} />
        </div>
      </div>
      <div className="li-sub faint" style={{ marginTop: 4, marginBottom: 10 }}>
        Where the driver app will send someone. Leave both empty until you have
        a real position — an invented one sends a car to the wrong place.
      </div>

      <label className="li-sub">Visible to drivers</label>
      <div className="chips" style={{ marginBottom: 4 }}>
        <Chip label="Test - console only" selected={(d.environment ?? 'test') === 'test'}
              onClick={() => set('environment')('test')} />
        <Chip label="Live - in the driver app" selected={d.environment === 'prod'}
              onClick={() => set('environment')('prod')} />
      </div>
      <div className="li-sub faint" style={{ marginTop: 4, marginBottom: 10 }}>
        The site governs. A charge point can only be live if the station it
        stands on is, so this is the one switch that takes a whole site off the
        app - or puts it there.
      </div>

      <label className="li-sub" htmlFor="st-timezone">Time zone</label>
      <input id="st-timezone" className="field mono" value={d.timezone ?? 'UTC'}
             placeholder="Africa/Addis_Ababa"
             onChange={(e) => set('timezone')(e.target.value)} />
      <div className="li-sub faint" style={{ marginTop: 4, marginBottom: 10 }}>
        Off-peak bands are wall-clock time at the site, not at the server.
      </div>

      <label className="li-sub" htmlFor="st-notes">Notes</label>
      <input id="st-notes" className="field" value={d.notes ?? ''}
             placeholder="Gate code, who holds the key, access hours"
             onChange={(e) => set('notes')(e.target.value)} />

      {(bad || error) && (
        <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginTop: 10 }}>
          {error ?? bad}
        </div>
      )}

      <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
        <button className="btn" style={{ flex: 1 }} disabled={!!bad || pending}
                onClick={() => onSubmit({ ...d, name, lat: latN, lng: lngN })}>
          {pending ? 'Saving...' : initial.id ? 'Save station' : 'Add station'}
        </button>
        <button className="btn ghost" style={{ flex: 1 }} onClick={onClose}>Cancel</button>
      </div>
    </Modal>
  );
}

/** The charge points standing on one site, with what each is doing. */
function OnSite({ points }: { points: ChargePoint[] }) {
  if (!points.length) {
    return (
      <div className="li-sub faint" style={{ marginTop: 8 }}>
        No charge points here yet. Assign one from the Charge points screen.
      </div>
    );
  }
  return (
    <div className="conn-list" style={{ marginTop: 8 }}>
      {points.map((cp) => (
        <div className="conn" key={cp.id}>
          <span className="conn-id">{cp.connectors.length}</span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div className="li-title">{cp.name}</div>
            <div className="li-sub mono">{cp.chargePointId}</div>
          </div>
          <Pill tone={chargePointTone(cp)}>{chargePointLabel(cp)}</Pill>
        </div>
      ))}
    </div>
  );
}

export function Stations() {
  const { data: stations, isLoading, error: loadError } = useStations();
  const { data: cpRes } = useChargePoints();
  const { create, update, archive } = useStationMutations();

  const [form, setForm] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<LiveSite | null>(null);
  /* The archive dialog's own refusal. It used to have none, so a failed
     archive left the dialog open with nothing said. */
  const [archiveError, setArchiveError] = useState<string | null>(null);
  const [filter, setFilter] = useState<'all' | 'empty'>('all');

  const points = cpRes?.items ?? [];
  const bySite = useMemo(() => {
    const m = new Map<string, ChargePoint[]>();
    for (const cp of points) {
      if (!cp.station) continue;
      m.set(cp.station.id, [...(m.get(cp.station.id) ?? []), cp]);
    }
    return m;
  }, [points]);

  if (isLoading) return <Loading />;
  // A failed load is said, not spun on forever.
  if (!stations) return <Empty title="Stations are not available." hint={loadFailedHint(loadError)} />;

  const unplaced = points.filter((cp) => !cp.station);
  const shown = filter === 'empty'
    ? stations.filter((st) => !(bySite.get(st.id)?.length))
    : stations;

  const connectors = points.reduce((n, cp) => n + cp.connectors.length, 0);
  const free = points.reduce((n, cp) => n + freeNow(cp), 0);

  const say = (e: Error) =>
    e.message === 'name_required' ? 'A station needs a name.'
      : e.message === 'coordinates_need_both' ? 'Latitude and longitude go together.'
        : errorText(e);

  return (
    <>
      <div className="tiles">
        <Tile k="Stations" v={stations.length} d={stations.length ? 'sites on the network' : 'none yet'} />
        <Tile k="Charge points" v={points.length} d={`${points.length - unplaced.length} placed`} />
        <Tile k="Unplaced" v={unplaced.length}
              d={unplaced.length ? 'on no station' : 'all accounted for'} />
        <Tile k="Connectors" v={connectors} d="bays across the network" />
        <Tile k="Free now" v={free} d="a driver could take" />
      </div>

      {/* The gap this screen exists to close. A charge point that registered
          itself and belongs to no address is invisible on a map and unbillable
          to a site host, so it is named here rather than left to be noticed. */}
      {!!unplaced.length && (
        <div className="note" style={{ marginTop: 12 }}>
          <b>{unplaced.length} charge point{unplaced.length === 1 ? '' : 's'} on no station.</b>{' '}
          {unplaced.map((cp) => cp.name).join(', ')}. A charger registers itself
          the first time it connects, so it arrives belonging to no address until
          someone places it. Assign them from the Charge points screen.
        </div>
      )}

      <div className="toolbar">
        <div className="chips">
          <Chip label={<>All <span className="mono">{stations.length}</span></>}
                selected={filter === 'all'} onClick={() => setFilter('all')} />
          <Chip
            label={<>Empty <span className="mono">
              {stations.filter((st) => !(bySite.get(st.id)?.length)).length}
            </span></>}
            selected={filter === 'empty'} onClick={() => setFilter('empty')} />
        </div>
        <button className="btn sm" onClick={() => { setForm({ ...EMPTY }); setError(null); }}>
          Add station
        </button>
      </div>

      {!shown.length && (
        <Empty
          title={stations.length ? 'No stations in this list.' : 'No stations yet.'}
          hint={stations.length
            ? 'Try All.'
            : 'A station is the site an address belongs to. Add one, then assign your charge points to it.'}
        />
      )}

      {!!shown.length && (
        <div className="station-grid">
          {shown.map((st) => {
            const here = bySite.get(st.id) ?? [];
            return (
              <div className="scard" key={st.id}>
                <div className="scard-head">
                  <div style={{ minWidth: 0 }}>
                    <div className="li-title">{st.name}</div>
                    <div className="li-sub">
                      {[st.address, st.city, st.country].filter(Boolean).join(', ')
                        || <span className="faint">no address yet</span>}
                    </div>
                    {st.lat != null && st.lng != null
                      ? <div className="li-sub mono">{st.lat.toFixed(4)}, {st.lng.toFixed(4)}</div>
                      : <div className="li-sub faint">no coordinates - drivers cannot be routed here</div>}
                  </div>
                  <Pill tone={here.length ? 'on' : 'off'}>
                    {here.length ? `${here.length} charge point${here.length === 1 ? '' : 's'}` : 'empty'}
                  </Pill>
                </div>

                <div className="scard-meta">
                  <Pill>{here.reduce((n, cp) => n + cp.connectors.length, 0)} connectors</Pill>
                  <Pill>{st.timezone}</Pill>
                </div>

                {st.notes && <div className="note">{st.notes}</div>}

                <OnSite points={here} />

                <div className="scard-actions">
                  <button className="btn sm ghost"
                          onClick={() => { setForm({ ...st }); setError(null); }}>
                    Edit
                  </button>
                  <button className="btn sm ghost"
                          onClick={() => { setArchiveError(null); setConfirming(st); }}>
                    Archive
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {form && (
        <StationForm
          initial={form}
          pending={create.isPending || update.isPending}
          error={error}
          onClose={() => setForm(null)}
          onSubmit={(v) => {
            const done = { onSuccess: () => setForm(null), onError: (e: Error) => setError(say(e)) };
            if (form.id) update.mutate({ ...v, id: form.id }, done);
            else create.mutate(v, done);
          }}
        />
      )}

      {confirming && (
        <Modal label="Archive station" onClose={() => setConfirming(null)}>
          <div className="li-title" style={{ fontSize: 15 }}>Archive {confirming.name}?</div>
          <div className="li-sub" style={{ marginTop: 6 }}>
            It stops appearing here. Nothing is deleted: transactions and
            settlement point back at a site, so the record has to survive.
          </div>
          <div className="note" style={{ marginTop: 10 }}>
            {(bySite.get(confirming.id)?.length ?? 0) > 0
              ? `The ${bySite.get(confirming.id)!.length} charge point(s) here are released, not archived. The hardware is still on the network and will show as unplaced.`
              : 'No charge points are assigned here.'}
          </div>
          {archiveError && (
            <div className="li-sub" role="alert" style={{ color: 'var(--bad)', marginTop: 8 }}>
              {archiveError}
            </div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 14 }}>
            <button className="btn" style={{ flex: 1 }} disabled={archive.isPending}
                    onClick={() => { setArchiveError(null); archive.mutate(confirming.id, {
                      onSuccess: () => setConfirming(null),
                      onError: (e: Error) => setArchiveError(errorText(e)),
                    }); }}>
              {archive.isPending ? 'Archiving...' : 'Archive station'}
            </button>
            <button className="btn ghost" style={{ flex: 1 }}
                    onClick={() => setConfirming(null)}>Cancel</button>
          </div>
        </Modal>
      )}
    </>
  );
}
